import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LuRefreshCw,
  LuLayers,
  LuContainer,
  LuZap,
  LuTriangle,
} from 'react-icons/lu';
import './PipelineStatus.css';
import TeamHeader from '../TeamHeader/TeamHeader';

// Icons

interface SwarmService {
  service: string;
  status: string;
  replicas: string;
  image: string;
  last_event_time: string;
}

interface ContainerMetric {
  service: string;
  status: string;
  state: string;
  cpu_pct: number;
  mem_usage_mb: number;
}

interface StatusData {
  swarm: any[];
  heartbeat: {
    table_name: string;
    health_score: string;
    last_lcc_detected: string;
    last_classified_stroke: string;
    last_grouping_op: string;
    updated_at: string;
  };
  solutions: any[];
  containers: any[];
}

const PipelineStatus = () => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<StatusData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Get the table from URL params, default to your specific table if missing
  const tableName =
    searchParams.get('table') || 'apache_solutions.usa_se_solutions';
  const fetchStatus = async () => {
    setLoading(true);
    try {
      // Adjust the URL to your actual API gateway/endpoint
      const response = await fetch(
        `pipelinemonitoring/status/?table=${tableName}`
      );
      if (!response.ok) throw new Error(`Error: ${response.statusText}`);

      const jsonData = await response.json();
      setData(jsonData);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Optional: Set up polling every 30 seconds for real-time monitoring
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [tableName]);

  if (loading && !data)
    return (
      <div className="notification-hub-wrapper">
        <TeamHeader title="Pipeline Status" />
        <div className="loading-state">
          <LuRefreshCw className="spin" /> Loading System Status...
        </div>
      </div>
    );
  if (error)
    return (
      <div className="notification-hub-wrapper">
        <TeamHeader title="Pipeline Status" />
        <div className="error-state">
          <LuTriangle /> {error}
        </div>
      </div>
    );
  if (!data) return null;
  return (
    <div className="notification-hub-wrapper">
      <TeamHeader title="Pipeline Status" />

      <div className="alert-side-menu-and-body">
        <div className="alert-body">
          <div className="customer-title-and-expand-buttons">
            <h1 className="cutomer-title">Pipeline</h1>
          </div>
          <div className="dashboard-main-grid">
            {/* 1. Recent Solutions Feed */}
            <section className="status-section">
              <h2 className="dashboard-grid-title">
                <LuZap /> Recent Solutions
              </h2>
              <div className="status-card pulse-card">
                <h3>Monitoring Table</h3>
                <code>{data.heartbeat.table_name}</code>
              </div>
              <div className="solutions-feed">
                {data.solutions.map((sol: any) => (
                  <div key={sol.id} className="sol-item">
                    <strong>ID {sol.id}</strong>
                    <span>
                      Lat: {sol.ltg_lat.toFixed(3)} | Lon:{' '}
                      {sol.ltg_lon.toFixed(3)}
                    </span>
                    <span className="text-muted">
                      {new Date(sol.dttime_utc).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            </section>
            {/* 2. Heartbeat & Pulse */}
            <section className="status-section heartbeat-grid">
              <div className="status-card pulse-card">
                <h3>Monitoring Table</h3>
                <code>{data.heartbeat.table_name}</code>
              </div>
              <div className="status-card pulse-card">
                <h3>Last LCC Detection</h3>
                <p>
                  {new Date(data.heartbeat.last_lcc_detected).toLocaleString()}
                </p>
              </div>
              <div className="status-card pulse-card">
                <h3>Last Classified Stroke</h3>
                <p>
                  {new Date(
                    data.heartbeat.last_classified_stroke
                  ).toLocaleString()}
                </p>
              </div>
              <div className="status-card pulse-card">
                <h3>Last Grouped Stroke</h3>
                <p>
                  {new Date(data.heartbeat.last_grouping_op).toLocaleString()}
                </p>
              </div>
            </section>
          </div>

          <div className="dashboard-main-grid">
            {/* 3. Swarm Services */}
            <section className="status-section">
              <h2 className="dashboard-grid-title">
                <LuLayers /> Swarm Services
              </h2>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>Status</th>
                      <th>Replicas</th>
                      <th>Image</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.swarm.map((s: SwarmService, i: number) => (
                      <tr
                        key={i}
                        className={s.status === 'DEGRADED' ? 'row-error' : ''}
                      >
                        <td>{s.service}</td>
                        <td>
                          <span
                            className={`status-pill ${s.status.toLowerCase()}`}
                          >
                            {s.status}
                          </span>
                        </td>
                        <td>{s.replicas}</td>
                        <td className="text-muted">
                          {s.image.split('/').pop()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* 4. Containers Health */}
            <section className="status-section">
              <h2 className="dashboard-grid-title">
                <LuContainer /> Container Metrics
              </h2>
              <div className="metrics-grid">
                {data.containers.map((c: ContainerMetric, i: number) => (
                  <div key={i} className="metric-tile">
                    <span className="metric-name">
                      {c.service || 'Unnamed'}
                    </span>
                    <div className="metric-stats">
                      <span>CPU: {c.cpu_pct}%</span>
                      <span>RAM: {c.mem_usage_mb.toFixed(1)} MB</span>
                    </div>
                    <div className={`mini-indicator ${c.state}`} />
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PipelineStatus;
